<template>
  <li :id="'chatMessageId' + message.id" class="message_item">
    <div class="message_box" :class="{
        selected_fw_msg: isSelected,
        new_live_message: message.is_new_message
    }">
      <!-- Multiple message selection -->
      <label v-if="multiMessageOptionEnabled" class="msg_f_label" :for="'msg_input_' + message.id" @click.stop>
        <input :id="'msg_input_' + message.id" type="checkbox" :checked="isSelected" class="message_selection_checkbox"
          @change="$emit('selection-change', message)" hidden>

        <span class="custom_checkbox_span">
          <i class="fa fa-check" aria-hidden="true" />
        </span>
      </label>

      <div :class="messageTypeClass">
        <!-- Deleted message -->
        <div v-if="message.delete_for_everyone || message.is_deleted" class="message_label_box deleted_message">
          <span class="chat_msg_content_show">
            <i class="fa fa-ban" aria-hidden="true" />
            This message was deleted
          </span>

          <span class="msg_time_wrp">
            <time class="msg_time">
              {{ formattedTime }}
            </time>
          </span>
        </div>

        <!-- Call alert -->
        <div v-else-if="message.for_call_alert" class="missed_called_label">
          <div class="missed_call_msg_box_wrp">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17" aria-hidden="true">
              <path fill="currentColor"
                d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z" />
            </svg>

            <span>Missed call at {{ formattedTime }}</span>
          </div>
        </div>

        <!-- Normal message -->
        <div v-else class="message_label_box">
          <!-- Sender name for group messages -->
          <span v-if="showSenderName" class="message_sender_name" :style="{ color: senderColor }">
            {{ senderName }}
          </span>

          <!-- Message actions -->
          <div class="dropdown message-actions">
            <button type="button" class="msg_toggle_option" data-toggle="dropdown" aria-label="Message actions">
              <i class="fa fa-chevron-down" aria-hidden="true" />
            </button>

            <ul class="dropdown-menu">
              <li @click="$emit('reply', message)">
                Reply
              </li>

              <li @click="$emit('forward', message)">
                Forward Message
              </li>

              <li @click="$emit('delete', message)">
                Delete Message
              </li>
            </ul>
          </div>

          <!-- Forwarded indicator -->
          <span v-if="message.forwarded" class="forwarded_msg_text">
            <i class="fa fa-share" aria-hidden="true" />
            <span>Forwarded</span>
          </span>

          <template v-if="displayMessage">
            <!-- File attachment -->
            <span v-if="displayMessage.file" class="msg_file_view">
              <!-- Image file -->
              <span v-if="isImageFile(displayMessage.file)" class="msg_img">
                <a :href="displayMessage.file" target="_blank" rel="noopener noreferrer">
                  <img :src="displayMessage.file" alt="Message attachment" class="img-fluid">
                </a>
              </span>

              <!-- Other file -->
              <span v-else class="msg_file_thumb">
                <a :href="displayMessage.file" target="_blank" rel="noopener noreferrer">
                  <img :src="checkFileExtension(displayMessage.file)" alt="" class="img-fluid">

                  <span class="msg_file_name">
                    {{ getFileName(displayMessage.file) }}
                  </span>
                </a>
              </span>
            </span>

            <!-- Message text -->
            <span v-if="displayMessage.message" class="chat_msg_content_show">
              <!-- Replied message preview -->
              <span v-if="displayMessage.reply_to_msg" class="show_msg_of_reply_wrp"
                :style="{ borderLeftColor: senderColor }" @click="$emit('replyMessageFullPreview', displayMessage.reply_to_msg)">
                <span class="show_msg_of_reply_name" :style="{ color: senderColor }">
                  {{ replySenderName(displayMessage.reply_to_msg) }}
                </span>

                <span class="show_msg_of_reply_txt">
                  <SafeMessageContent :message="replyPreview(displayMessage.reply_to_msg)" />
                </span>
              </span>

              <!-- Main message content -->
              <SafeMessageContent :message="String(displayMessage.message)" />
            </span>
          </template>

          <!-- Message time and status -->
          <span class="msg_time_wrp">
            <time class="msg_time">
              {{ formattedTime }}
            </time>

            <span v-if="isOwnMessage" class="indication_label">
              <i class="msg_indication" :class="statusClass" aria-hidden="true" />
            </span>
          </span>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import SafeMessageContent from './SafeMessageContent';
import {
    checkFileExtension,
    getFileName,
    isImageFile
} from '@/utils/chatFile';

export default {
  name: 'ChatMessageItem',

  components: {
    SafeMessageContent
  },

  props: {
    message: {
      type: Object,
      required: true
    },

    selectedRoomInfo: {
      type: Object,
      required: true
    },

    selfUserId: {
      type: [String, Number],
      required: true
    },

    multiMessageOptionEnabled: {
      type: Boolean,
      default: false
    },

    selectedMessages: {
      type: Array,
      default: function () {
        return [];
      }
    },
  },

  computed: {
    sender() {
      return this.message.sender || {};
    },

    isOwnMessage() {
      return String(this.sender.user_id) === String(this.selfUserId);
    },

    showSenderName() {
      return Boolean(
        this.selectedRoomInfo &&
        this.selectedRoomInfo.is_group_chat &&
        !this.isOwnMessage
      );
    },

    senderName() {
      return [
        this.sender.first_name,
        this.sender.last_name
      ].filter(Boolean).join(' ');
    },

    senderColor() {
      return this.sender.colour || '#888888';
    },

    messageTypeClass() {
      return this.isOwnMessage ? 'sent' : 'replies';
    },

    displayMessage() {
      if (
        this.message.forwarded &&
        this.message.forwarded_from
      ) {
        return this.message.forwarded_from;
      }

      return this.message;
    },

    formattedTime() {
      var timestamp = this.message.timestamp;

      if (!timestamp) {
        return '';
      }

      var date = new Date(timestamp);

      if (isNaN(date.getTime())) {
        return '';
      }

      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    messageStatus() {
      return this.getMessageStatusMethod(this.message.message_statuses);
    },

    statusClass() {
      if (this.messageStatus === 'read') {
        return 'msg_read_ind';
      }

      if (this.messageStatus === 'delivered') {
        return 'msg_sent_ind';
      }

      return 'msg_unsent_ind';
    },

    isSelected() {
      return this.selectedMessages.some((item) => {
        return String(item.id) === String(this.message.id);
      });
    }
  },

  methods: {
    checkFileExtension,
    getFileName,
    isImageFile,
    getMessageStatusMethod(messageStatuses) {
    if (
        !Array.isArray(messageStatuses) ||
        messageStatuses.length === 0
    ) {
        return 'undelivered';
    }

    const allRead = messageStatuses.every((status) => {
        return status && status.is_read === true;
    });

    if (allRead) {
        return 'read';
    }

    const allDelivered = messageStatuses.every((status) => {
        return (
            status &&
            (
                status.is_delivered === true ||
                status.is_read === true
            )
        );
    });

    return allDelivered
        ? 'delivered'
        : 'undelivered';
},
    replySenderName(reply) {
      var sender = reply && reply.sender
        ? reply.sender
        : {};

      return [
        sender.first_name,
        sender.last_name
      ].filter(Boolean).join(' ');
    },

    replyPreview(reply) {
      if (!reply) {
        return '';
      }

      var source = reply.forwarded && reply.forwarded_from
        ? reply.forwarded_from
        : reply;

      if (source.message) {
        return String(source.message);
      }

      return this.getFileName(source.file);
    },
  }
};
</script>
<style lang="scss" scoped>
@keyframes zoom-in-zoom-out {
    0% {
        transform: scale(0);
        -webkit-transform: scale(0);
        -moz-transform: scale(0)
    }

    100% {
        transform: scale(1);
        -webkit-transform: scale(1);
        -moz-transform: scale(1)
    }
}
// Individual message list item
.message_item {
  clear: both;
  float: left;
  margin: 5px 0;
  width: 100%;
  font-size: 13px;

  // Wrapper for sent and received messages
  .message_box {
    position: relative;

    // Main message bubble
    .message_label_box {
      background: linear-gradient(180deg, #d0efff, #a0e0ff);
      color: #000000;
      border-radius: 0 7px 7px 7px;
      text-align: left;
      display: inline-block;
      padding: 5px 8px;
      line-height: 130%;
      margin: 0;
      max-width: calc(100% - 60px);
      word-break: break-word;
      white-space: normal;
      width: auto;
      position: relative;
      font-weight: 500;

      // Message action dropdown wrapper
      .dropdown {
        position: absolute;
        right: 5px;
        top: 5px;
        display: none;

        // Dropdown toggle button
        .msg_toggle_option {
          cursor: pointer;
          background: rgba(255, 255, 255, 0.1);
          line-height: 1;
          display: block;
          padding: 0 1px;
          border: none;
        }

        // Message action dropdown menu
        .dropdown-menu {
          padding: 5px 0;
          margin: 0;
          background-color: rgba(0, 0, 0, 0.92);
          border: 1px solid rgba(255, 255, 255, 0.15);

          // Individual dropdown menu option
          li {
            margin: 0 !important;
            padding: 5px 10px;
            color: #fff;
            font-size: 15px !important;
            line-height: 1.3;
            cursor: pointer;

            // Dropdown option hover state
            &:hover {
              color: #ffd400;
            }
          }
        }
      }

      // Show message actions when hovering over the bubble
      &:hover .dropdown {
        display: block;
      }

      // Common message bubble tail
      &:after {
        position: absolute;
        content: "";
        width: 0;
        height: 0;
        border-style: solid;
      }
      // Message sender name
      .message_sender_name {
        display: block;
        margin-top: -1px;
        font-size: 14px;
        color: #7e009a;
        font-family: 'MyriadProRegular', sans-serif;
        font-weight: 600;
        letter-spacing: 0.5px;
        margin-bottom: 2px;
        padding-right: 15px;
      }

      // Message timestamp and delivery status wrapper
      .msg_time_wrp {
        display: block;
        text-align: right;
        text-transform: uppercase;
        margin-top: 2px;
        line-height: 1;
        font-size: 10px;

        // Message timestamp
        .msg_time {
          font-size: 10px;
          line-height: 1;
          color: rgba(0, 0, 0, 0.5);
          font-weight: 500;
          display: inline-block;
          vertical-align: middle;
        }
      }

      // Replied message preview container
      .show_msg_of_reply_wrp {
        background: #ffd9de;
        padding: 3px 8px;
        border-radius: 5px;
        border-left: 3px solid #87cefa;
        display: block;
        margin: 0 -5px 3px;
        font-weight: 400;
        cursor: pointer;

        // Original sender name in reply preview
        .show_msg_of_reply_name {
          display: block;
          color: #565656;
          font-weight: 500;
          font-size: 14px;
          margin-bottom: 2px;
          line-height: 1;
        }

        // Original message content in reply preview
        .show_msg_of_reply_txt {
          .safe_message_content {
            color: #6f6f6f;
            font-size: 13px;
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            line-height: 1.3;
          }

          // Link inside replied message content
          a {
            color: #6f6f6f;
            text-decoration: none;
          }
        }
      }

      // Forwarded message indicator
      .forwarded_msg_text {
        display: block;
        text-align: left;

        // Forwarded message label
        & > span {
          display: inline-block;
          vertical-align: middle;
          margin-left: 3px;
          font-weight: 400;
          font-style: italic;
          font-size: 12px;
          color: rgba(0, 0, 0, 0.5);
          font-family: Arial, sans-serif;
        }

        // Forwarded message icon
        & >.fa {
          color: rgba(0, 0, 0, 0.5);
          font-size: 14px;
          display: inline-block;
          vertical-align: middle;
        }
      }

      // Message sent, delivered, and read status wrapper
      .indication_label {
        display: inline-block;
        margin: 0 0 0 3px;
        vertical-align: middle;
        line-height: 8px;

        // Base message status indicator
        .msg_indication {
          display: inline-block;
          width: 8px;
          height: 8px;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 50%;
          vertical-align: middle;

          // Message read status
          &.msg_read_ind {
            background: #358815;
            border-color: #2a7d1a;
          }

          // Message not sent status
          &.msg_unsent_ind {
            border-color: #616f67;
          }

          // Message sent status
          &.msg_sent_ind {
            background: #0f6281;
            border-color: #0f6281;
          }

          // Force message read status
          &.force_read {
            background: #358815 !important;
            border-color: #2a7d1a !important;
          }
        }

        // Font Awesome delivery status icons
        .fa {
          font-size: 12px;
          line-height: 1;

          // Checkered flag status
          &.fa-flag-checkered {
            color: #2c5770;
          }

          // Filled flag status
          &.fa-flag {
            color: #05cc47;
          }

          // Outlined flag status
          &.fa-flag-o {
            color: #616f67;
          }
        }
      }

      // Deleted message display
      &.deleted_message {
        padding: 5px 8px;
        font-size: 15px;
        color: #4c4c4c;

        // Deleted chat message link
        .msg_link_chat {
          padding-right: 0 !important;
          color: #4c4c4c !important;
        }

        // Sender name inside deleted message
        .message_sender_name {
          padding-right: 0;
        }

        // Deleted message content
        .msg_link {
          padding-right: 0;
          color: #4c4c4c;
        }
        // File content inside deleted message
        .msg_file_view {
          padding: 0 5px;
        }
      }
      // File content inside deleted message
      .msg_file_view {
        padding: 0;
        background: none;
        border-radius: 0;
        display: table;
        height: 100%;
        float: none;
        width: 100%;
        text-align: center;
        margin: 0 auto 2px;
        .msg_img{
          height: 210px;
          min-width: 230px;
          display: block;
          margin: 0 auto;
          max-width: 230px;
          .img-fluid {
              width: 100%;
              height: 100%;
              object-fit: cover;
              object-position: center center;
              border-radius: 3px;
          }
        }
        .msg_file_thumb{
          a{
            color: #000000;
            text-decoration: none;
            display: flex;
            text-align: left;
            align-items: center;
            background: rgba(0, 0, 0, .1);
            border-radius: 4px;
            padding: 3px;
            min-height: 46px;
            .img-fluid {
                width: 40px;
            }
            .msg_file_name {
                line-height: 1.3;
                display: inline-block;
                padding:0px 2px;
                font-size: 13px;
                font-weight: 500;
            }
          }
        }
      }

    }

    // missed called label alignment and styling
    .missed_called_label {
      text-align: center;
      padding: 2px 0;

      .missed_call_msg_box_wrp {
        padding: 5px 12px 6px;
        text-align: center;
        background-color: #b1d4e5;
        border-radius: 4.5px;
        box-shadow: 0 1px 0.5px rgba(255, 255, 255, 0.5);
        display: inline-block;

        svg {
          color: #df3333;
          margin-right: 7px;
        }

        span {
          color: #000;
          font-size: 14px;
          font-weight: 500;
        }
      }
    }

    // Sent message alignment and styling
    .sent {
      text-align: right;

      // Sent message bubble
      .message_label_box {
        background: linear-gradient(180deg, #ffe7eb, #ffa2b0);
        border-radius: 7px 0 7px 7px;
        text-align: left;

        // Sent message bubble tail
        &:after {
          border-width: 0 0 8px 6px;
          border-color: transparent transparent transparent #ffe7eb;
          top: 0;
          right: -5px;
        }
      }
    }

    // Received message styling
    .replies {

      // Received message bubble tail
      .message_label_box:after {
        border-width: 0 6px 8px 0;
        border-color: transparent #e7f6fd transparent transparent;
        top: 0;
        left: -5px;
      }

      .show_msg_of_reply_wrp {
        background: rgba(159, 179, 189, 0.55) !important;
        border-left: 3px solid rgba(255, 182, 193, 0.62)
      }
    }
    &.new_live_message{
      .sent{
       .message_label_box {
        animation: zoom-in-zoom-out .5s ease-out;
        transform-origin: right bottom;
        }
      }
    }
  }
   // Forward Message selection wrapper
  .selected_fw_msg{
    .sent{
      background: rgba(255,182,193,.25);
      border-radius: 5px;
    }
    .replies{
      background: rgba(177,212,229,.25);
      border-radius: 5px;
    }
  }
  // Multi-message selection checkbox wrapper
  .msg_f_label {
    margin: 0;
    position: absolute;
    top: 50%;
    left: -24px;
    transform: translateY(-50%);
    -webkit-transform: translateY(-50%);
    -moz-transform: translateY(-50%);
    width: 16px;
    height: 16px;

    // Custom checkbox appearance
    .custom_checkbox_span {
      display: flex;
      width: 16px;
      height: 16px;
      border: 2px solid #ffc107;
      border-radius: 2px;
      line-height: 16px;
      justify-content: center;
      cursor: pointer;

      // Checkbox checkmark icon
      .fa {
        color: #000000;
        font-size: 0;
      }
    }

    // Checked checkbox state
    input[type="checkbox"]:checked+.custom_checkbox_span {
      background-color: #ffc107;
      border-color: #ffc107;

      // Display the checkmark when selected
      .fa {
        font-size: 14px;
        line-height: 1;
      }
    }
  }
}


// .msg_img_render_in_chat {
//     height: 210px;
//     min-width: 230px;
//     display: block;
//     margin: 0 auto;
//     max-width: 230px;
// }

// .msg_img_render_in_chat .img-fluid {
//     width: 100%;
//     height: 100%;
//     object-fit: cover;
//     object-position: center center;
//     border-radius: 3px
// }

</style>